import { findLastIndex } from 'ramda';
import { WATER_DENSITY } from 'utils/backfill';
import { distanceMetricToImperial } from 'utils/unitConversions';
import { convertkPaToM } from '../canvas/Model/Telemetry/conversions';
import { FlushProfile, FlushProfilePoint } from '../MineModel/Flushing/flushTypes';

export interface FlushOverlayInput {
  profile: FlushProfile;
  // Front position at which the air purge can start, from the solved plan; undefined when unreachable.
  airStartDistance?: number;
  // Front position at which the flush pump takes over from the paste pump; undefined when it drives from the start.
  handoverDistance?: number;
}

export interface ChartPoint {
  x: number;
  y: number;
  kpa: number;
}

// A pipe pressure rating along the route, in metres and kPa, in route order (stope -> plant). A node
// where the pipe changes contributes two entries at the same distance, its stope-side pipe first.
export interface RatingPoint {
  distance: number;
  elevation: number;
  kpa: number;
}

export interface FlushOverlaySeries {
  // The flush HGL in route order (stope -> plant). Head is metres of the local fluid, so the front is
  // two points at the same x: the paste side below, the water side above.
  hglData: ChartPoint[];
  // Pressure at each x, for the tooltip.
  kpaByX: Map<number, number>;
  // The water side of the front.
  waterFront: ChartPoint;
  // Distance (display units) at which the air purge can start; drawn as a vertical line.
  airStartX?: number;
  // Distance (display units) at which the flush pump takes over; drawn as a vertical line.
  handoverX?: number;
  // The pipe rating in the same local-fluid basis, stepping up where the water starts.
  pipeRatingData: ChartPoint[];
  // The rating of the pipe spanning the front when the front is between nodes, for the tooltip.
  frontRatingKpa?: number;
}

// Water behind the front, paste from the front onwards.
export const flushDensityAt = ({ frontDistance }: FlushProfile, slurryDensity: number) => (distance: number) => (
  distance < frontDistance ? WATER_DENSITY : slurryDensity
);

// Maps a solved flush profile onto the HGL chart. Head is drawn in metres of the fluid at each point
// (water behind the front, paste ahead of it), so the flush HGL and the pipe rating both step at the
// front: pressure is continuous there, but the column of water it supports is taller than the paste's.
export const buildFlushOverlay = (
  { profile, airStartDistance, handoverDistance }: FlushOverlayInput,
  slurryDensity: number,
  isImperial: boolean,
  pipeRating: RatingPoint[] = [],
): FlushOverlaySeries => {
  const { points, frontDistance, frontElevation, frontPressureKpa } = profile;
  const toDisplay = (metres: number) => (isImperial ? distanceMetricToImperial(metres) : metres);
  const chartPoint = (distance: number, elevation: number, kpa: number, density: number): ChartPoint => ({
    x: toDisplay(distance),
    y: toDisplay(convertkPaToM(kpa, density)) + toDisplay(elevation),
    kpa,
  });
  const densityAt = flushDensityAt(profile, slurryDensity);
  const nodePoint = (point: FlushProfilePoint) => chartPoint(point.distanceFromPump, point.elevation, point.pressureKpa, densityAt(point.distanceFromPump));
  const frontPoint = (density: number) => chartPoint(frontDistance, frontElevation, frontPressureKpa, density);

  const pasteAhead = points.some((point) => point.distanceFromPump > frontDistance);
  const waterBehind = points.some((point) => point.distanceFromPump < frontDistance);
  // A node sitting on the front is represented by the front points themselves.
  const frontPoints = [
    ...(pasteAhead || !waterBehind ? [frontPoint(slurryDensity)] : []),
    ...(waterBehind ? [frontPoint(WATER_DENSITY)] : []),
  ];
  const hglData = [
    ...points.filter((point) => point.distanceFromPump > frontDistance).map(nodePoint),
    ...frontPoints,
    ...points.filter((point) => point.distanceFromPump < frontDistance).map(nodePoint),
  ];

  const kpaByX = new Map<number, number>(points.map((point) => [toDisplay(point.distanceFromPump), point.pressureKpa]));
  kpaByX.set(toDisplay(frontDistance), frontPressureKpa);

  // The stope-side rating of a node on the front is still under paste; anything else at that distance is under water.
  const firstOnFront = pipeRating.findIndex((point) => point.distance === frontDistance);
  const ratingDensity = (point: RatingPoint, index: number) => {
    if (point.distance !== frontDistance) return densityAt(point.distance);
    return index === firstOnFront && pasteAhead ? slurryDensity : WATER_DENSITY;
  };
  // Between nodes the front lies inside the pump-side pipe of the nearest node ahead, the last entry ahead of it.
  const lastAhead = findLastIndex((point: RatingPoint) => point.distance > frontDistance, pipeRating);
  const frontRatingKpa = firstOnFront < 0 && lastAhead >= 0 && waterBehind ? pipeRating[lastAhead].kpa : undefined;
  const ratingStep = frontRatingKpa === undefined
    ? []
    : [chartPoint(frontDistance, frontElevation, frontRatingKpa, slurryDensity), chartPoint(frontDistance, frontElevation, frontRatingKpa, WATER_DENSITY)];
  const pipeRatingData = pipeRating.flatMap((point, index) => [
    chartPoint(point.distance, point.elevation, point.kpa, ratingDensity(point, index)),
    ...(index === lastAhead ? ratingStep : []),
  ]);

  return {
    hglData,
    kpaByX,
    waterFront: frontPoints[frontPoints.length - 1],
    airStartX: airStartDistance !== undefined ? toDisplay(airStartDistance) : undefined,
    handoverX: handoverDistance !== undefined ? toDisplay(handoverDistance) : undefined,
    pipeRatingData,
    frontRatingKpa,
  };
};
